import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Check, ChevronDown, History, Monitor, Smartphone, Tablet, X } from "lucide-react";
import { rhm, dt } from "../api";
import { Badge, Btn, Field, Input, Modal, Select, Textarea } from "../ui";
import { ImageField } from "./MediaLibrary";
import BlogBody, { tocFromBlocks } from "../../components/blog/BlogBody";

export const Panel = ({ title, children, defaultOpen = false, testId }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <section className="border border-white/10 bg-bg1" data-testid={testId}>
            <button onClick={() => setOpen((v) => !v)} data-testid={`${testId}-toggle`}
                className="w-full flex items-center justify-between gap-3 px-4 py-3">
                <span className="mono text-[10px] tracking-[0.18em] text-zinc-300">{title.toUpperCase()}</span>
                <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
        </section>
    );
};

const DOT = { good: "bg-lime", warn: "bg-orange-400", bad: "bg-red-500" };

export const SeoPanel = ({ post, report, onChange, canEditSeo }) => {
    const seo = post.seo || {};
    const setSeo = (patch) => onChange({ seo: { ...seo, ...patch } });
    const groups = [...new Set(report.checks.map((c) => c.group))];
    const tone = report.score >= 80 ? "text-lime" : report.score >= 55 ? "text-orange-300" : "text-red-400";

    return (
        <section className="border border-white/10 bg-bg1" data-testid="seo-panel">
            <header className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                <span className="mono text-[10px] tracking-[0.18em] text-zinc-300">SEO CHECK</span>
                <span className={`font-display text-2xl ${tone}`} data-testid="seo-score">{report.score}/100</span>
            </header>
            <div className="p-4 space-y-4">
                <div className="text-xs text-zinc-500">
                    {report.words} words · about {report.reading_minutes} min read · checks run on your device, no AI credits used.
                </div>

                <Field label="Primary SEO keyword" hint="Everything below is measured against this phrase">
                    <Input value={seo.focus_keyword || ""} disabled={!canEditSeo} data-testid="seo-keyword"
                        onChange={(e) => setSeo({ focus_keyword: e.target.value })} placeholder="hair fall treatment" />
                </Field>
                <Field label="SEO title" hint={`${(seo.seo_title || post.title || "").length} characters`}>
                    <Input value={seo.seo_title || ""} disabled={!canEditSeo} data-testid="seo-title"
                        onChange={(e) => setSeo({ seo_title: e.target.value })} placeholder={post.title} />
                </Field>
                <Field label="Meta description" hint={`${(seo.meta_description || "").length} / 160 characters`}>
                    <Textarea rows={3} value={seo.meta_description || ""} disabled={!canEditSeo} data-testid="seo-meta"
                        onChange={(e) => setSeo({ meta_description: e.target.value })} />
                </Field>
                <Field label="Canonical URL" hint="Leave empty unless this article is published elsewhere">
                    <Input value={seo.canonical_url || ""} disabled={!canEditSeo} data-testid="seo-canonical"
                        onChange={(e) => setSeo({ canonical_url: e.target.value })} />
                </Field>
                <label className="flex items-center justify-between gap-3 text-sm">
                    <span>Index this article in search engines</span>
                    <input type="checkbox" checked={!seo.noindex} disabled={!canEditSeo} data-testid="seo-index"
                        onChange={(e) => setSeo({ noindex: !e.target.checked })} />
                </label>

                <div className="pt-3 border-t border-white/10 space-y-2">
                    <div className="mono text-[9px] tracking-[0.2em] text-zinc-500">SOCIAL SHARING</div>
                    <Input value={seo.og_title || ""} disabled={!canEditSeo} placeholder="Open Graph title (optional)"
                        data-testid="seo-og-title" onChange={(e) => setSeo({ og_title: e.target.value })} />
                    <Textarea rows={2} value={seo.og_description || ""} disabled={!canEditSeo}
                        placeholder="Open Graph description (optional)" data-testid="seo-og-desc"
                        onChange={(e) => setSeo({ og_description: e.target.value })} />
                    <Input value={post.social_image || ""} placeholder="Social share image URL (defaults to featured image)"
                        data-testid="seo-social-image" onChange={(e) => onChange({ social_image: e.target.value })} />
                </div>

                <div className="pt-3 border-t border-white/10 space-y-3" data-testid="seo-checks">
                    {groups.map((g) => (
                        <div key={g}>
                            <div className="mono text-[9px] tracking-[0.2em] text-zinc-500 mb-1.5">{g.toUpperCase()}</div>
                            <div className="space-y-1.5">
                                {report.checks.filter((c) => c.group === g).map((c) => (
                                    <div key={c.id} className="flex gap-2 items-start" data-testid={`seo-check-${c.id}`}>
                                        <span className={`w-1.5 h-1.5 mt-1.5 rounded-full shrink-0 ${DOT[c.status]}`} />
                                        <div className="min-w-0">
                                            <div className="text-xs text-zinc-300">{c.label}</div>
                                            <div className="text-[11px] text-zinc-500">{c.message}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export const SettingsColumn = ({ post, meta, products, areas, allPosts, onChange, can }) => {
    const styles = meta?.styles || [];
    const tagInput = React.useRef(null);
    return (
        <div className="space-y-3">
            <Panel title="Category & tags" defaultOpen testId="panel-category">
                <Field label="Category">
                    <Select value={post.category_id || ""} data-testid="post-category"
                        onChange={(e) => onChange({ category_id: e.target.value })}>
                        <option value="">Choose a category</option>
                        {(meta?.categories || []).filter((c) => c.is_active !== false).map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </Select>
                </Field>
                <Field label="Tags" hint="Optional. Press Enter to add.">
                    <Input ref={tagInput} placeholder="hair fall" data-testid="post-tag-input"
                        onKeyDown={(e) => {
                            if (e.key !== "Enter") return;
                            e.preventDefault();
                            const v = e.currentTarget.value.trim();
                            if (v && !(post.tags || []).includes(v)) onChange({ tags: [...(post.tags || []), v] });
                            e.currentTarget.value = "";
                        }} />
                </Field>
                <div className="flex flex-wrap gap-2">
                    {(post.tags || []).map((t) => (
                        <span key={t} className="inline-flex items-center gap-1.5 border border-white/12 px-2 py-1 text-xs"
                            data-testid={`post-tag-${t}`}>
                            {t}
                            <button onClick={() => onChange({ tags: post.tags.filter((x) => x !== t) })}
                                className="text-zinc-500 hover:text-red-400"><X className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
            </Panel>

            <Panel title="Author" defaultOpen testId="panel-author">
                <Select value={post.author_id || ""} data-testid="post-author"
                    onChange={(e) => onChange({ author_id: e.target.value })}>
                    <option value="">Choose an author</option>
                    {(meta?.authors || []).filter((a) => a.is_active !== false).map((a) => (
                        <option key={a.id} value={a.id}>{a.name}{a.title ? ` — ${a.title}` : ""}</option>
                    ))}
                </Select>
                <p className="text-[11px] text-zinc-500">
                    Choosing an author does not give anyone permission to publish.
                </p>
            </Panel>

            <Panel title="Featured image" defaultOpen testId="panel-featured-image">
                <ImageField label="Shown on the blog list and social shares" value={post.featured_image || {}}
                    testId="post-featured-image" onChange={(v) => onChange({ featured_image: v })} />
            </Panel>

            <Panel title="Article style" testId="panel-style">
                <Select value={post.style || "standard"} data-testid="post-style"
                    onChange={(e) => onChange({ style: e.target.value })}
                    options={styles.map((s) => ({ value: s.key, label: s.label }))} />
                <p className="text-[11px] text-zinc-500">
                    {styles.find((s) => s.key === (post.style || "standard"))?.description}
                </p>
            </Panel>

            <Panel title="Reading experience" testId="panel-reading">
                <label className="flex items-center justify-between gap-3 text-sm">
                    <span>Show “In this article” list</span>
                    <input type="checkbox" checked={post.toc_enabled !== false} data-testid="post-toc"
                        onChange={(e) => onChange({ toc_enabled: e.target.checked })} />
                </label>
                <label className="flex items-center justify-between gap-3 text-sm">
                    <span>Show health disclaimer</span>
                    <input type="checkbox" checked={post.disclaimer_enabled !== false} data-testid="post-disclaimer"
                        onChange={(e) => onChange({ disclaimer_enabled: e.target.checked })} />
                </label>
                <Field label="Custom disclaimer for this article" hint="Leave empty to use the blog-wide disclaimer">
                    <Textarea rows={3} value={post.disclaimer_text || ""} data-testid="post-disclaimer-text"
                        onChange={(e) => onChange({ disclaimer_text: e.target.value })} />
                </Field>
                <label className="flex items-center justify-between gap-3 text-sm">
                    <span>Feature on the homepage</span>
                    <input type="checkbox" checked={!!post.is_featured} data-testid="post-featured"
                        onChange={(e) => onChange({ is_featured: e.target.checked })} />
                </label>
            </Panel>

            <Panel title="Products & consultation" testId="panel-links">
                <Field label="Related products" hint="Used by the sidebar and Product → Related Blogs in the CRM">
                    <div className="space-y-1.5">
                        {(products || []).map((p) => (
                            <label key={p.slug} className="flex items-center gap-2 text-sm" data-testid={`post-product-${p.slug}`}>
                                <input type="checkbox" checked={(post.product_slugs || []).includes(p.slug)}
                                    onChange={(e) => onChange({ product_slugs: e.target.checked
                                        ? [...(post.product_slugs || []), p.slug]
                                        : (post.product_slugs || []).filter((s) => s !== p.slug) })} />
                                {p.name}
                            </label>
                        ))}
                    </div>
                </Field>
                <Field label="Consultation CTA area">
                    <Select value={post.consultation_area || ""} data-testid="post-consult-area"
                        onChange={(e) => onChange({ consultation_area: e.target.value })}>
                        <option value="">General consultation</option>
                        {(areas || []).map((a) => <option key={a.key || a.id} value={a.key || a.id}>{a.name}</option>)}
                    </Select>
                </Field>
            </Panel>

            <Panel title="Related articles" testId="panel-related">
                <p className="text-[11px] text-zinc-500 mb-2">
                    Leave empty and ReHealthMe picks articles from the same category automatically.
                </p>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {(allPosts || []).filter((p) => p.id !== post.id).map((p) => (
                        <label key={p.id} className="flex items-center gap-2 text-xs" data-testid={`post-related-${p.slug}`}>
                            <input type="checkbox" checked={(post.related_post_ids || []).includes(p.id)}
                                onChange={(e) => onChange({ related_post_ids: e.target.checked
                                    ? [...(post.related_post_ids || []), p.id]
                                    : (post.related_post_ids || []).filter((x) => x !== p.id) })} />
                            <span className="truncate">{p.title}</span>
                        </label>
                    ))}
                </div>
            </Panel>

            <Panel title="Medical review" testId="panel-review">
                <Field label="Review status">
                    <Select value={post.review?.status || "not_reviewed"} data-testid="post-review-status"
                        onChange={(e) => onChange({ review: { ...(post.review || {}), status: e.target.value } })}
                        options={[{ value: "not_reviewed", label: "Not reviewed" },
                            { value: "under_review", label: "Under review" },
                            { value: "reviewed", label: "Reviewed by a qualified professional" }]} />
                </Field>
                <Field label="Reviewer">
                    <Input value={post.review?.reviewer || ""} data-testid="post-reviewer"
                        onChange={(e) => onChange({ review: { ...(post.review || {}), reviewer: e.target.value } })} />
                </Field>
                <Field label="Review date">
                    <Input type="date" value={(post.review?.reviewed_at || "").slice(0, 10)} data-testid="post-review-date"
                        onChange={(e) => onChange({ review: { ...(post.review || {}), reviewed_at: e.target.value } })} />
                </Field>
                <p className="text-[11px] text-zinc-500">
                    Only tick “Reviewed” when a qualified professional has actually checked the article.
                </p>
            </Panel>
        </div>
    );
};

const DEVICES = [
    { key: "desktop", label: "Desktop", icon: Monitor, width: "100%" },
    { key: "tablet", label: "Tablet", icon: Tablet, width: "834px" },
    { key: "mobile", label: "Mobile", icon: Smartphone, width: "390px" },
];

export const PreviewDialog = ({ post, meta, onClose }) => {
    const [device, setDevice] = useState("desktop");
    const author = (meta?.authors || []).find((a) => a.id === post.author_id) || {};
    const category = (meta?.categories || []).find((c) => c.id === post.category_id);
    const toc = post.toc_enabled !== false ? tocFromBlocks(post.blocks) : [];
    return (
        <Modal open onClose={onClose} wide title="Preview" testId="blog-preview">
            <div className="flex items-center gap-2 mb-4">
                {DEVICES.map((d) => (
                    <button key={d.key} onClick={() => setDevice(d.key)} data-testid={`preview-device-${d.key}`}
                        className={`inline-flex items-center gap-2 px-3 py-2 border mono text-[10px] tracking-[0.16em] ${
                            device === d.key ? "border-gold text-gold bg-gold/10" : "border-white/15 text-zinc-400"}`}>
                        <d.icon className="w-3.5 h-3.5" /> {d.label.toUpperCase()}
                    </button>
                ))}
            </div>
            <div className="mx-auto bg-bg0 border border-white/10" style={{ width: DEVICES.find((d) => d.key === device).width, maxWidth: "100%" }}>
                <div className="p-6">
                    {category && <div className="inline-block bg-gold/15 text-gold px-3 py-1 mono text-[9px] tracking-[0.2em]">
                        {category.name.toUpperCase()}</div>}
                    <h1 className="mt-4 font-display text-3xl uppercase tracking-tight">{post.title}</h1>
                    {post.excerpt && <p className="mt-3 text-zinc-400 text-sm">{post.excerpt}</p>}
                    <div className="mt-4 mono text-[10px] tracking-[0.16em] text-zinc-500">
                        {author.is_brand ? author.name : author.name ? `BY ${author.name.toUpperCase()}` : "NO AUTHOR SELECTED"}
                        {" · "}{post.reading_minutes || 1} MIN READ
                    </div>
                    {post.featured_image?.url && (
                        <img src={post.featured_image.url} alt={post.featured_image.alt || ""}
                            className="mt-6 w-full aspect-[16/9] object-cover border border-white/10" />
                    )}
                    {toc.length >= 2 && (
                        <div className="mt-6 border border-white/10 p-4">
                            <div className="mono text-[10px] tracking-[0.2em] text-gold mb-2">IN THIS ARTICLE</div>
                            <ol className="space-y-1 text-sm text-zinc-300">
                                {toc.map((t, i) => <li key={t.id} className={t.level === 3 ? "pl-4" : ""}>
                                    <span className="mono text-[10px] text-zinc-600 mr-2">{String(i + 1).padStart(2, "0")}</span>{t.text}</li>)}
                            </ol>
                        </div>
                    )}
                    <div className="mt-6">
                        <BlogBody blocks={post.blocks || []} style={post.style} />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export const PublishDialog = ({ post, report, onClose, onPublish, canPublish, canSchedule }) => {
    const [mode, setMode] = useState("now");
    const [when, setWhen] = useState("");
    const checklist = [
        { label: "Title added", ok: !!post.title },
        { label: "Featured image added", ok: !!(post.featured_image || {}).url },
        { label: "Category selected", ok: !!post.category_id },
        { label: "Author selected", ok: !!post.author_id },
        { label: "SEO title checked", ok: !!(post.seo?.seo_title || post.title) },
        { label: "Meta description added", ok: !!post.seo?.meta_description },
        { label: "Primary keyword checked", ok: !!post.seo?.focus_keyword },
        { label: "URL confirmed", ok: !!post.slug },
        { label: "Disclaimer setting confirmed", ok: post.disclaimer_enabled !== undefined },
    ];
    return (
        <Modal open onClose={onClose} title="Publishing checklist" testId="publish-dialog">
            <div className="space-y-2" data-testid="publish-checklist">
                {checklist.map((c) => (
                    <div key={c.label} className="flex items-center gap-2 text-sm">
                        {c.ok ? <Check className="w-4 h-4 text-lime" /> : <AlertTriangle className="w-4 h-4 text-orange-400" />}
                        <span className={c.ok ? "text-zinc-300" : "text-orange-300"}>{c.label}</span>
                    </div>
                ))}
            </div>
            <div className="mt-4 border border-white/10 p-3 flex items-center justify-between">
                <span className="mono text-[10px] tracking-[0.16em] text-zinc-400">SEO SCORE</span>
                <span className="font-display text-xl text-gold">{report.score}/100</span>
            </div>
            <p className="text-xs text-zinc-500 mt-2">
                These are recommendations — you can publish even if some items are not ticked.
            </p>

            <div className="mt-5 flex gap-2">
                <button onClick={() => setMode("now")} data-testid="publish-mode-now"
                    className={`flex-1 border px-4 py-2.5 mono text-[10px] tracking-[0.16em] ${
                        mode === "now" ? "border-gold text-gold" : "border-white/15 text-zinc-400"}`}>PUBLISH NOW</button>
                <button onClick={() => setMode("schedule")} data-testid="publish-mode-schedule"
                    className={`flex-1 border px-4 py-2.5 mono text-[10px] tracking-[0.16em] ${
                        mode === "schedule" ? "border-gold text-gold" : "border-white/15 text-zinc-400"}`}>SCHEDULE</button>
            </div>
            {mode === "schedule" && (
                <div className="mt-4">
                    <Field label="Publish at" hint="Uses your device time zone. The article goes live on its own.">
                        <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
                            data-testid="publish-schedule-at" />
                    </Field>
                </div>
            )}

            <div className="mt-6 flex justify-end gap-2">
                <Btn variant="ghost" onClick={onClose}>CANCEL</Btn>
                <Btn data-testid="publish-confirm"
                    disabled={mode === "now" ? !canPublish : !canSchedule || !when}
                    onClick={() => onPublish(mode === "schedule" ? new Date(when).toISOString() : "")}>
                    {mode === "schedule" ? "SCHEDULE ARTICLE" : "PUBLISH NOW"}
                </Btn>
            </div>
        </Modal>
    );
};

export const VersionsDialog = ({ postId, onClose, onRestored, canRestore }) => {
    const { data: versions = [] } = useQuery({
        queryKey: ["blog-versions", postId],
        queryFn: async () => (await rhm.get(`/blog/posts/${postId}/versions`)).data,
    });
    return (
        <Modal open onClose={onClose} title="Version history" testId="versions-dialog">
            {!versions.length && <div className="text-sm text-zinc-500">No saved versions yet. A version is stored each time you publish.</div>}
            <div className="space-y-2">
                {versions.map((v) => (
                    <div key={v.id} className="flex items-center justify-between gap-3 border border-white/10 px-3 py-2.5"
                        data-testid={`version-${v.id}`}>
                        <div className="min-w-0">
                            <div className="text-sm">{v.label} · v{v.version}</div>
                            <div className="mono text-[9px] tracking-[0.14em] text-zinc-500">
                                {dt(v.created_at)} · {v.user_name || "—"}
                            </div>
                        </div>
                        {canRestore && (
                            <Btn variant="ghost" data-testid={`version-restore-${v.id}`}
                                onClick={() => onRestored(v.id)}><History className="w-3.5 h-3.5 mr-1 inline" /> RESTORE</Btn>
                        )}
                    </div>
                ))}
            </div>
            <div className="mt-4"><Badge tone="muted">RESTORING REPLACES THE CURRENT CONTENT</Badge></div>
        </Modal>
    );
};
